import { useMemo, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import logger from '@widgets/logger';
import InputAdornment from '@mui/material/InputAdornment';
import { styled } from '@mui/material/styles';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import { Typography } from '@components/Typography';
import ImageUploaderSingle from '@components/ImageUploaderSingle';
import { MB } from '@constants/file';
import { CHANNEL } from '@constants/channels';
import { useTranslation, useThrottle, useCopyToClipboard, useLocations, useChannels } from '@hooks';
import { micrositesService } from '@services';
import { micrositesActions } from '@actions/microsites';
import { CopyIcon } from 'assets/images/icons';
import Subscription from './Subscription';
import { StyledSwitch } from './styles';

const StyledSlugInput = styled(Input)({
    '& .MuiInputLabel-root': {
        transform: 'translate(278px, 12px)',
    },
    '& .MuiOutlinedInput-notchedOutline legend span': {
        padding: 0,
    },
    '& .MuiInputBase-adornedStart fieldset legend': {
        padding: '0 7px',
    },
});

const CssStartAdornment = styled(InputAdornment)({
    margin: 0,
    '& .MuiTypography-root': {
        fontSize: '14px',
    },
});

const General = ({ isView, selectedMicrosite }) => {
    const [isUploading, setIsUploading] = useState(false);

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { control, setValue, watch } = useFormContext();
    const { onCopy } = useCopyToClipboard();
    const [onCopyClick] = useThrottle(value => onCopy(value), 1000);
    const { getSelectLocationsOptions } = useLocations();
    const { getChannelsByLocationId, getChannelOptions } = useChannels();

    const locationId = watch('locationId');
    const slug = watch('slug');
    const logoUrl = watch('logoUrl');
    const showLiveChat = watch('showLiveChat');

    useEffect(() => {
        dispatch(micrositesActions.getLocationReviews(locationId));
    }, [locationId]);

    const companyOptions = useMemo(() => getSelectLocationsOptions(), []);

    const channelsOptions = useMemo(() => {
        const filterCb = c => c.type === CHANNEL.LIVE_CHAT;
        const availableChannels = getChannelsByLocationId(locationId, filterCb);
        return getChannelOptions({ in: true, out: true }, availableChannels);
    }, [locationId]);

    const uploadLogo = async e => {
        try {
            setIsUploading(true);
            const data = new FormData();
            data.append('file', e.target.files[0]);
            data.append('fileName', e.target.files[0].name);
            const response = await micrositesService.uploadMicrositeImage(data, selectedMicrosite.id);
            setValue('logoUrl', response.publicUrl, { shouldDirty: true });
        } catch (_) {
            logger.error("Couldn't upload logo microsite");
        } finally {
            setIsUploading(false);
            e.target.value = '';
        }
    };

    const onDeleteLogo = () => setValue('logoUrl', null, { shouldDirty: true });

    return (
        <>
            <div style={{ margin: '8px 0' }}>
                <Controller
                    control={control}
                    name="name"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('MicrositesPage.codeName')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                            disabled={isView}
                            sx={{ marginBottom: '8px' }}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="locationId"
                    render={({ field, ref }) => (
                        <Select
                            {...field}
                            onChange={e => field.onChange(e.target.value)}
                            ref={ref}
                            label={t('common.location')}
                            fullWidth
                            options={companyOptions}
                            disabled={isView}
                            sx={{ marginBottom: '8px' }}
                            groupBased
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="slug"
                    render={({ field, fieldState: { error }, ref }) => (
                        <StyledSlugInput
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('common.url')}
                            startAdornment={
                                <CssStartAdornment position="start">
                                    {`${process.env.REACT_APP_SURVEY_DOMAIN}/`}
                                </CssStartAdornment>
                            }
                            onAddonClick={() => onCopyClick(`${process.env.REACT_APP_SURVEY_DOMAIN}/${slug}`)}
                            addonComponent={<CopyIcon style={{ fill: 'rgba(0, 0, 0, 0.6)' }} />}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                            disabled={isView}
                        />
                    )}
                />
            </div>
            <Typography variant="subtitle1">{t('common.logo')}</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.6)', margin: '4px 0 8px' }}>
                {t('MicrositesPage.recommendedDimension')}
            </Typography>
            <ImageUploaderSingle
                maxSize={10 * MB}
                logoUrl={logoUrl}
                disabled={isUploading || isView}
                onDelete={onDeleteLogo}
                uploadLogo={uploadLogo}
                isUploading={isUploading}
            />
            <Typography variant="subtitle1" sx={{ paddingTop: '32px', color: 'rgba(0, 0, 0, 0.87)' }}>
                {t('MicrositesPage.additionalSettings')}
            </Typography>
            <Controller
                control={control}
                name="socialMediaEnable"
                render={({ field, ref }) => (
                    <StyledSwitch
                        {...field}
                        ref={ref}
                        checked={field.value}
                        handleChange={event => {
                            setValue('socialMediaEnable', event.target.checked, { shouldDirty: true });
                        }}
                        label={t('MicrositesPage.displaySocialMediaLinks')}
                        disabled={isView}
                    />
                )}
            />
            <Controller
                control={control}
                name="showReviews"
                render={({ field, ref }) => (
                    <StyledSwitch
                        {...field}
                        ref={ref}
                        checked={field.value}
                        handleChange={event => {
                            setValue('showReviews', event.target.checked, { shouldDirty: true });
                        }}
                        label={t('MicrositesPage.showReviews')}
                        disabled={isView}
                    />
                )}
            />
            <Controller
                control={control}
                name="showLiveChat"
                render={({ field, ref }) => (
                    <StyledSwitch
                        {...field}
                        ref={ref}
                        checked={field.value}
                        handleChange={event => {
                            setValue('showLiveChat', event.target.checked, { shouldDirty: true, shouldValidate: true });
                            if (!event.target.checked) {
                                setValue('liveChatChannelId', '', { shouldDirty: true, shouldValidate: true });
                            }
                        }}
                        label={t('MicrositesPage.showLiveChat')}
                        disabled={isView}
                    />
                )}
            />
            {showLiveChat && (
                <Controller
                    control={control}
                    name="liveChatChannelId"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Select
                            {...field}
                            onChange={({ target: { value } }) => {
                                setValue('liveChatChannelId', value, { shouldValidate: true, shouldDirty: true });
                            }}
                            ref={ref}
                            label={t('common.channel')}
                            fullWidth
                            options={channelsOptions}
                            required
                            style={{ marginTop: '8px' }}
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                        />
                    )}
                />
            )}
            <Subscription />
        </>
    );
};

export default General;
